import { useEffect, useState } from 'react';
import { feeBookApi, memberApi, type FeeBookEntry, type Member } from '../api';
import Select from '../components/common/Select';

// Traditional SA advocate fee book — chronological ledger of earned fees for
// SARS submissions and Bar records. Derives from `fee_book_entries` which is
// written at invoice-issue time.

export default function FeeBook() {
  const thisYear = new Date().getUTCFullYear();
  const [from, setFrom] = useState(`${thisYear}-03-01`); // SA tax year starts March
  const [to, setTo] = useState(`${thisYear + 1}-02-28`);
  const [counsel, setCounsel] = useState<number | 'all'>('all');
  const [entries, setEntries] = useState<FeeBookEntry[]>([]);
  const [totals, setTotals] = useState({ entries: 0, hours: 0, amount_zar: 0 });
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await feeBookApi.list({
        from, to,
        counsel: counsel === 'all' ? undefined : counsel,
      });
      setEntries(r.entries);
      setTotals(r.totals);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    memberApi.list().then((r) => setMembers(r.members)).catch(() => null);
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, counsel]);

  const counselName = (user_id: number) => {
    const m = members.find((x) => x.user_id === user_id);
    return m?.display_name || `#${user_id}`;
  };

  return (
    <div className="stack">
      <div className="row">
        <div className="grow">
          <h1>Fee book</h1>
          <p className="muted">Chronological ledger of earned fees for the selected tax period. Auto-updates when invoices are issued.</p>
        </div>
      </div>

      <div className="card">
        <div className="row">
          <div className="grow">
            <label>From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="grow">
            <label>To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="grow">
            <label>Counsel</label>
            <Select
              value={counsel === 'all' ? 'all' : String(counsel)}
              onChange={(v) => setCounsel(v === 'all' ? 'all' : Number(v))}
              options={[
                { value: 'all', label: 'All counsel' },
                ...members.map((m) => ({ value: String(m.user_id), label: m.display_name })),
              ]}
              searchPlaceholder="Search counsel\u2026"
            />
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
        <MetricCard label="Entries"    value={String(totals.entries)} />
        <MetricCard label="Total hours" value={totals.hours.toFixed(2)} />
        <MetricCard label="Total (ZAR)" value={`R ${totals.amount_zar.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`} />
      </div>

      {error && <div className="card" style={{ borderColor: 'var(--color-danger)' }}>{error}</div>}

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="muted" style={{ padding: 14 }}>Loading\u2026</div>
        ) : entries.length === 0 ? (
          <p className="muted" style={{ padding: 14 }}>No fee-book entries in this period. Issue an invoice to see entries appear here.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 110 }}>Date</th>
                <th>Description</th>
                <th>Matter</th>
                <th>Counsel</th>
                <th style={{ textAlign: 'right', width: 80 }}>Hours</th>
                <th style={{ textAlign: 'right', width: 140 }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.ROWID}>
                  <td>{e.entry_date}</td>
                  <td>{e.description}</td>
                  <td>#{e.matter_id}</td>
                  <td>{counselName(e.counsel_user_id)}</td>
                  <td style={{ textAlign: 'right' }}>{e.hours ? Number(e.hours).toFixed(2) : '\u2014'}</td>
                  <td style={{ textAlign: 'right' }}>R {Number(e.amount_zar).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="card">
      <div style={{ color: 'var(--color-text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, marginTop: 6 }}>{value}</div>
    </div>
  );
}
